import { useEffect, useState } from "react";
import Icon from "../../../components/base/Icon";
import DropdownMenu, { MenuItem } from "../../../components/menu/DropdownMenu";
import { API_ROUTES } from "../../../core/Constants";
import { addOrUpdateChat, hasRole, openChatAccess, openChatRename, openDeleteChat, openSettingsChat, openTechCalendarModal, removeChat, useHub } from "../../../core/Global";
import ChatContactItem from "../models/ChatContactItem"
import { toastSuccess } from "../../../components/base/Toast";


interface IProps {
    chatId?: string
    chat?: ChatContactItem
    onPin?: () => void
    onUnpin?: () => void
    onUnarchive?: () => void
    // The chat was blocked or unblocked, so it leaves the list it is shown in.
    onBlockChange?: () => void
}

/**
 * Fired after a block or unblock, with detail { chatId, blocked }: the archived view recounts its
 * Blocked row and an open chat updates its header.
 */
export const CHAT_BLOCK_CHANGED_EVENT = "chat-block-changed"

type Hub = ReturnType<typeof useHub>

/** Blocks the chat and takes it out of the live list. */
export const blockChat = (hub: Hub, chat: ChatContactItem | undefined, done?: () => void) => {
    hub.Put(API_ROUTES.Chat + "block/" + chat?.chatId, {}, res => {
        if (!res.success) return
        removeChat(chat?.chatId)
        toastSuccess("Marked as spam. Its messages are kept under Archived › Spam.")
        window.dispatchEvent(new CustomEvent(CHAT_BLOCK_CHANGED_EVENT, { detail: { chatId: chat?.chatId, blocked: true } }))
        if (done) done()
    })
}

/** Unblocks the chat and puts it back in the live list. */
export const unblockChat = (hub: Hub, chat: ChatContactItem | undefined, done?: () => void) => {
    hub.Put(API_ROUTES.Chat + "unblock/" + chat?.chatId, {}, res => {
        if (!res.success) return
        if (chat) addOrUpdateChat(res.data ?? { ...chat, archived: false, blocked: false })
        toastSuccess("Removed from spam. The chat is back in the chat list.")
        window.dispatchEvent(new CustomEvent(CHAT_BLOCK_CHANGED_EVENT, { detail: { chatId: chat?.chatId, blocked: false } }))
        if (done) done()
    })
}

export default function ChatContactContextMenu(props: IProps) {

    const hub = useHub();

    const [menu, menuSet] = useState<MenuItem[]>([])

    useEffect(() => {
        var m: MenuItem[] = [];

        if ((props.chat?.unreadCount ?? 0) > 0) {
            m.push({
                title: "Claim All",
                icon: "check",
                action() {
                    hub.Get(API_ROUTES.Chat + "claimall/" + props.chatId, res => {
                        if (res.success) {
                        }
                    })
                },
            })
        }

        if (props.chat?.pinned) {
            m.push({
                title: "Unpin Chat",
                icon: "thumbtack",
                action() {
                    hub.Get(API_ROUTES.Chat + "unpin/" + props.chatId, res => {
                        if (res.success) {
                            if (props.onUnpin) props.onUnpin()
                        }
                    })
                },
            })
        }
        else if (!props.chat?.archived) {
            m.push({
                title: "Pin Chat",
                icon: "thumbtack",
                action() {
                    hub.Get(API_ROUTES.Chat + "pin/" + props.chatId, res => {
                        if (res.success) {
                            if (props.onPin) props.onPin()
                        }
                        else if (res.message == "pinnedexceed") {
                            window.openModal("modal-pinned-chats-exceeed");
                        }
                    })
                },
            })
        }

        if (hasRole("ManageChatsAccess")) {
            m.push({
                title: "Chat Access",
                icon: "users-cog",
                action() {
                    if (props.chat) openChatAccess(props.chat)
                },
            })
        }

        if (!props.chat?.isSystem) {
            m.push({
                title: "Chat Settings",
                icon: "cog",
                action() {
                    if (props.chat) openSettingsChat(props.chat)
                },
            })
        }

        if (props.chat?.tech && !props.chat.isGroup) {

            m.push({
                title: "Tech Calendar",
                icon: "calendar",
                action() {
                    openTechCalendarModal(props.chat?.tech?.id, props.chat?.tech?.fullName)
                },
            })
        }

        // System chats (Office, Rinvox) can be archived too; a new message brings them back.
        if (hasRole("ArchiveChat") && !props.chat?.archived) {
            m.push({
                title: "Archive Chat",
                icon: "archive",
                action() {
                    hub.Put(API_ROUTES.Chat + "archive/" + props.chat?.id, {}, res => {
                        // The live hub says the same thing to every session; this keeps the
                        // list correct here even when the socket is down.
                        if (res.success) removeChat(props.chat?.chatId)
                    })
                },
            })
        }

        if (hasRole("ArchiveChat") && props.chat?.archived && !props.chat?.blocked) {
            m.push({
                title: "Unarchive Chat",
                icon: "arrow-up-from-bracket",
                action() {
                    hub.Put(API_ROUTES.Chat + "unarchive/" + props.chat?.id, {}, res => {
                        if (res.success && props.chat) addOrUpdateChat(res.data ?? { ...props.chat, archived: false })
                        if (props.onUnarchive) props.onUnarchive();
                    })
                },
            })
        }

        if (hasRole("ArchiveChat") && !props.chat?.isSystem) {
            m.push({
                title: "Rename Chat",
                icon: "pencil",
                action() {
                    if (props.chat) openChatRename(props.chat)
                },
            })
        }

        // Twilio cannot refuse an SMS, so a blocked number still writes in: its chat just stays
        // out of the list and raises no notifications.
        if (hasRole("ArchiveChat") && props.chat?.canBlock && !props.chat?.blocked) {
            m.push({
                title: "Mark as Spam",
                icon: "ban",
                warning: true,
                action() {
                    blockChat(hub, props.chat, props.onBlockChange)
                },
            })
        }

        if (hasRole("ArchiveChat") && props.chat?.blocked) {
            m.push({
                title: "Not Spam",
                icon: "circle-check",
                action() {
                    unblockChat(hub, props.chat, props.onBlockChange)
                },
            })
        }

        if (hasRole("DeleteChat") && !props.chat?.isSystem) {
            m.push({
                title: "Delete Chat",
                icon: "circle-minus",
                danger: true,
                action() {
                    if (props.chat) openDeleteChat(props.chat)
                },
            })
        }

        menuSet(f => [...m])


    }, [props.chat])

    return (
        <DropdownMenu width={180} source={menu} />
    )
}